import React, { useEffect, useRef, useState } from 'react';
import Modal from '../../design-system/components/atoms/modal';
import Divider from '../../../components/settings/components/my-profile/components/change-email-modal/change-email-modal-flow/sub-components/divider';
import { submitCrashReportFeedback } from '../../utils/posthog-error-reporting';
import { CrashReportDetailFormProps } from './types';
import Button from '../../design-system/components/atoms/button';

const CrashReportDetailForm = ({
  crashReportId,
  errorMessage,
  componentStack,
  t,
}: CrashReportDetailFormProps) => {
  const [feedback, setFeedback] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [feedbackModalDismissed, setFeedbackModalDismissed] = useState(false);
  const prevCrashReportIdRef = useRef<string | null | undefined>();

  const canSubmitFeedback = Boolean(crashReportId && feedback.trim());

  useEffect(() => {
    if (
      crashReportId &&
      !prevCrashReportIdRef.current &&
      !feedbackModalDismissed &&
      !submitted
    ) {
      setShowFeedbackModal(true);
    }
    prevCrashReportIdRef.current = crashReportId;
  }, [crashReportId, feedbackModalDismissed, submitted]);

  const onFeedbackChange = (
    event: React.ChangeEvent<HTMLTextAreaElement>,
  ): void => {
    setFeedback(event.target.value);
  };

  const onCloseFeedbackModal = (): void => {
    setShowFeedbackModal(false);
    setFeedbackModalDismissed(true);
  };

  const onSubmitFeedback = (): void => {
    if (submitted) {
      onCloseFeedbackModal();
      return;
    }

    if (!crashReportId || !feedback.trim()) {
      return;
    }

    submitCrashReportFeedback({
      reportId: crashReportId,
      feedback: feedback.trim(),
      errorMessage,
      componentStack,
    });

    setSubmitted(true);
  };

  return (
    <Modal
      show={showFeedbackModal}
      className="bs-error-boundary-feedback-modal"
      titleContent={
        <span className="semibold-4 gray-txt-9">
          {t('messages.error_boundary_feedback_title')}
        </span>
      }
      titleDescription={
        <>
          <span>
            Our team has been notified. If you'd like to help, tell us what
            happened below.
          </span>
          <Divider />
        </>
      }
      backdrop="static"
      backdropClassName="bs-error-boundary-feedback-modal-backdrop"
      onClose={onCloseFeedbackModal}
      onCancel={onCloseFeedbackModal}
      onHide={onCloseFeedbackModal}
      onSubmit={onSubmitFeedback}
      submitButtonText={
        submitted
          ? t('messages.error_boundary_feedback_done')
          : t('messages.error_boundary_feedback_submit')
      }
      cancelButtonText={t('labels.cancel')}
      cancelButtonVarient={Button.Variant.Outlined}
      hideCancelButton={submitted}
      isSubmitDisabled={!submitted && !canSubmitFeedback}
    >
      {submitted ? (
        <span className="bs-error-boundary-feedback-success-text">
          {t('messages.error_boundary_feedback_success')}
        </span>
      ) : (
        <>
          <span className="bs-error-boundary-textarea-title">WHAT HAPPENED?</span>
          <textarea
            className="form-control blue-txt-15 regular-2 w-100 bs-error-boundary-feedback-modal-textarea"
            rows={4}
            value={feedback}
            onChange={onFeedbackChange}
            placeholder={t('messages.error_boundary_feedback_placeholder')}
            maxLength={500}
          />
          <Divider />
        </>
      )}
    </Modal>
  );
};

export default CrashReportDetailForm;
